import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { addDays, addWeeks, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useTareas } from '../../context/TareasContext';
import { useAuth } from '../../context/AuthContext';
import { Casa } from '../../types';
import {
  INICIALES_DIA, Ocurrencia, claveDia, colorDe, esMia, estaAtrasada, lunesDe, mediodia, ocurrencias, yoEn,
} from '../../utils/tareas';
import { TarjetaTarea } from '../../components/tareas/TarjetaTarea';
import { ModalTarea } from '../../components/tareas/ModalTarea';
import { ModalAccionesTarea } from '../../components/tareas/ModalAccionesTarea';
import { ModalPlantillaTareas } from '../../components/tareas/ModalPlantillaTareas';
import { ModalAvisosTareas } from '../../components/tareas/ModalAvisosTareas';
import { ModalBorrarTareas } from '../../components/tareas/ModalBorrarTareas';
import { ModalCalendarioTareas } from '../../components/tareas/ModalCalendarioTareas';
import { ElegirParticipanteCasa } from '../../components/tareas/ElegirParticipanteCasa';
import { TarjetaReparto } from '../../components/tareas/TarjetaReparto';
import { TarjetaEstrellas } from '../../components/tareas/TarjetaEstrellas';
import { botonPrincipal, botonSecundario, chip } from '../../components/tareas/ui';
import { Bell, CalendarDays, ChevronLeft, ChevronRight, ListChecks, Plus, Sparkles, Trash2 } from 'lucide-react';
import { esApp } from '../../utils/plataforma';
import { leerAjustesTareas } from '../../utils/avisosTareas';
import { vibrar } from '../../utils/hapticos';

// Ajustes de la vista de este dispositivo (no hace falta que viajen)
const CLAVE_CASA = 'rzcentral.tareas.casa';
const CLAVE_MIAS = 'rzcentral.tareas.mias';
const leer = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const guardar = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* sin almacenamiento */ } };

/**
 * La semana común: lo que toca cada día en las casas del usuario, con el
 * color de cada persona. En el móvil se elige el día en una tira; en el iPad
 * y el ordenador se ven los siete a la vez.
 */
export const Semana: React.FC = () => {
  const { user } = useAuth();
  const { casas, tareas, hechas, cargando, crearCasaPersonal, marcarHecha, casaConPlus } = useTareas();

  const [lunes, setLunes] = useState(() => lunesDe(new Date()));
  const [diaElegido, setDiaElegido] = useState(() => mediodia(new Date()));
  const [filtroCasa, setFiltroCasa] = useState(() => leer(CLAVE_CASA) ?? 'todas');
  const [soloMias, setSoloMias] = useState(() => leer(CLAVE_MIAS) === 'si');
  const [nueva, setNueva] = useState<{ fecha?: Date } | null>(null);
  const [editando, setEditando] = useState<Ocurrencia | null>(null);
  const [abierta, setAbierta] = useState<Ocurrencia | null>(null);
  const [plantilla, setPlantilla] = useState(false);
  const [avisos, setAvisos] = useState(false);
  const [borrarVarias, setBorrarVarias] = useState(false);
  const [calendario, setCalendario] = useState(false);
  // La campana va en naranja si los avisos de este iPhone están encendidos
  const [avisosActivos, setAvisosActivos] = useState(false);
  useEffect(() => {
    if (esApp && !avisos) leerAjustesTareas().then(a => setAvisosActivos(a.activos)).catch(() => {});
  }, [avisos]);

  // «Mis tareas»: la casa personal se crea sola la primera vez
  const creandoPersonal = useRef(false);
  useEffect(() => {
    if (cargando || creandoPersonal.current || casas.some(c => c.personal && c.creadorUid === user?.uid)) return;
    creandoPersonal.current = true;
    crearCasaPersonal().catch(e => { console.warn('No se pudo crear «Mis tareas»', e); creandoPersonal.current = false; });
  }, [cargando, casas, crearCasaPersonal, user]);

  const casaPorId = useMemo(() => new Map(casas.map(c => [c.id, c])), [casas]);
  const casasVistas = filtroCasa === 'todas' ? casas : casas.filter(c => c.id === filtroCasa);
  // Si la casa guardada ya no existe (saliste de ella), se vuelve a todas
  useEffect(() => {
    if (!cargando && filtroCasa !== 'todas' && !casas.some(c => c.id === filtroCasa)) setFiltroCasa('todas');
  }, [cargando, casas, filtroCasa]);

  const idsVistos = new Set(casasVistas.map(c => c.id));
  const filtrar = (o: { tarea: { casaId: string } } & Partial<Ocurrencia>) => {
    if (!idsVistos.has(o.tarea.casaId)) return false;
    if (!soloMias) return true;
    const casa = casaPorId.get(o.tarea.casaId);
    // Lo hecho es «mío» si lo hice yo
    if (o.hecha) return o.hecha.uid === user?.uid || o.hecha.participanteId === yoEn(casa, user?.uid)?.id;
    return esMia(o.tarea as any, casa, user?.uid);
  };

  const domingo = addDays(lunes, 6);
  const hoy = mediodia(new Date());
  const semana = useMemo(
    () => ocurrencias(tareas, hechas, lunes, domingo, hoy).filter(o => filtrar(o)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tareas, hechas, lunes, filtroCasa, soloMias, casas, user]);
  const atrasadas: Ocurrencia[] = useMemo(() => tareas
    .filter(t => estaAtrasada(t, hoy))
    .map(t => ({ clave: `${t.id}:atrasada`, tarea: t, fecha: t.fecha, estado: 'pendiente' as const }))
    .filter(o => filtrar(o))
    .sort((a, b) => a.fecha.getTime() - b.fecha.getTime()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tareas, filtroCasa, soloMias, casas, user]);

  const dias = Array.from({ length: 7 }, (_, i) => addDays(lunes, i));
  const delDia = (d: Date) => semana.filter(o => claveDia(o.fecha) === claveDia(d));
  const esEstaSemana = claveDia(lunes) === claveDia(lunesDe(hoy));
  const sinReclamar = casas.filter(c => !c.personal && !yoEn(c, user?.uid));
  const hayTareas = tareas.some(t => idsVistos.has(t.casaId));
  // El reparto solo tiene sentido en casas de más de una persona y con tareas
  const compartidasConTareas = casasVistas.filter(c =>
    !c.personal && c.participantes.filter(p => p.activo).length > 1 && tareas.some(t => t.casaId === c.id));
  const casaParaNueva = filtroCasa !== 'todas' ? filtroCasa : (casas.find(c => !c.personal) ?? casas[0])?.id;

  const irASemana = (l: Date) => {
    setLunes(l);
    // En el móvil se queda elegido el mismo día de la semana
    setDiaElegido(addDays(l, (claveDia(l) === claveDia(lunesDe(hoy)) ? (hoy.getDay() + 6) % 7 : 0)));
  };

  const marcarRapido = (o: Ocurrencia) => {
    const casa = casaPorId.get(o.tarea.casaId);
    marcarHecha(o.tarea, yoEn(casa, user?.uid)?.id ?? o.tarea.asignadoA)
      .then(() => vibrar.exito())
      .catch(e => { console.error(e); vibrar.error(); });
  };

  const tarjeta = (o: Ocurrencia, extra: { conDia?: boolean; compacta?: boolean } = {}) => (
    <TarjetaTarea key={o.clave} o={o} casa={casaPorId.get(o.tarea.casaId)} conCasa={casasVistas.length > 1}
      onMarcar={() => marcarRapido(o)} onAbrir={() => setAbierta(o)} {...extra} />
  );

  if (cargando && casas.length === 0) {
    return <div className="max-w-5xl mx-auto px-4 py-10 text-gray-500 dark:text-gray-400">Cargando…</div>;
  }

  const titulo = `${format(lunes, 'd', { locale: es })}${lunes.getMonth() !== domingo.getMonth() ? format(lunes, " 'de' MMM", { locale: es }) : ''} – ${format(domingo, "d 'de' MMMM", { locale: es })}`;
  const personas: { casa: Casa; id: string; nombre: string }[] = casasVistas
    .filter(c => !c.personal)
    .flatMap(c => c.participantes.filter(p => p.activo).map(p => ({ casa: c, id: p.id, nombre: p.nombre })));

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <header className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tareas</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
            Lo que toca en casa esta semana, y a quién.{' '}
            <Link to="/ayuda#tareas" className="font-semibold text-lumbre-700 dark:text-lumbre-400 hover:underline">
              ¿Cómo funciona?
            </Link>
          </p>
        </div>
        <div className="flex gap-2">
          {hayTareas && (
            <button onClick={() => setBorrarVarias(true)} className={botonSecundario}
              aria-label="Borrar varias tareas" title="Borrar varias tareas">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {hayTareas && (
            <button onClick={() => setCalendario(true)} className={botonSecundario}
              aria-label="Calendario y Recordatorios" title="Calendario y Recordatorios">
              <CalendarDays className="w-4 h-4" />
            </button>
          )}
          {esApp && (
            <button onClick={() => setAvisos(true)} aria-pressed={avisosActivos}
              aria-label={avisosActivos ? 'Avisos de tareas (activados)' : 'Avisos de tareas (desactivados)'}
              className={avisosActivos ? `${botonSecundario} !bg-lumbre-600 !border-lumbre-600 !text-white hover:!bg-lumbre-700` : botonSecundario}>
              <Bell className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => setNueva({ fecha: esEstaSemana ? undefined : diaElegido })} className={botonPrincipal}>
            <Plus className="w-4 h-4" /> Nueva tarea
          </button>
        </div>
      </header>

      {sinReclamar.map(c => <div key={c.id} className="mb-4"><ElegirParticipanteCasa casa={c} /></div>)}

      {/* ── Filtros ───────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4">
        {casas.length > 1 && (
          <>
            <button onClick={() => { setFiltroCasa('todas'); guardar(CLAVE_CASA, 'todas'); }} className={chip(filtroCasa === 'todas')}>
              Todas
            </button>
            {casas.map(c => (
              <button key={c.id} onClick={() => { setFiltroCasa(c.id); guardar(CLAVE_CASA, c.id); }} className={chip(filtroCasa === c.id)}>
                {c.icono} {c.nombre}
              </button>
            ))}
            <span className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-1" aria-hidden />
          </>
        )}
        <button onClick={() => { setSoloMias(v => !v); guardar(CLAVE_MIAS, soloMias ? 'no' : 'si'); }}
          aria-pressed={soloMias} className={chip(soloMias)}>
          Solo las mías
        </button>
      </div>

      {personas.length > 1 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 mb-4 text-xs text-gray-600 dark:text-gray-400">
          {personas.map(p => (
            <span key={`${p.casa.id}:${p.id}`} className="inline-flex items-center gap-1">
              <span className={`w-2.5 h-2.5 rounded-full ${colorDe(p.casa, p.id).punto}`} aria-hidden />
              {p.nombre}{casasVistas.filter(c => !c.personal).length > 1 && <span className="opacity-70">({p.casa.nombre})</span>}
            </span>
          ))}
        </div>
      )}

      {!hayTareas ? (
        <div className="text-center py-14 bg-white dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
          <ListChecks className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-700 dark:text-gray-300 font-medium">Todavía no hay tareas.</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
            Empieza por las habituales (fregar, la basura, la lavadora…) o crea las tuyas. Para repartirlas, crea una casa
            en «Casas» e invita a quien viva contigo.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            <button onClick={() => setPlantilla(true)} className={botonPrincipal}>
              <Sparkles className="w-4 h-4" /> Elegir las habituales
            </button>
            <button onClick={() => setNueva({})} className={botonSecundario}>
              <Plus className="w-4 h-4" /> Crear una
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* ── Atrasadas ──────────────────────────────────────────────────── */}
          {atrasadas.length > 0 && (
            <section className="mb-5">
              <h2 className="text-sm font-bold text-red-700 dark:text-red-400 mb-2">
                Atrasadas ({atrasadas.length})
              </h2>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {atrasadas.map(o => tarjeta(o, { conDia: true }))}
              </div>
            </section>
          )}

          {/* ── Navegación de semanas ──────────────────────────────────────── */}
          <div className="flex items-center justify-between gap-2 mb-3">
            <button onClick={() => irASemana(addWeeks(lunes, -1))} aria-label="Semana anterior"
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <p className="font-bold text-gray-900 dark:text-white first-letter:uppercase">{titulo}</p>
              {!esEstaSemana && (
                <button onClick={() => irASemana(lunesDe(hoy))}
                  className="text-xs font-semibold text-lumbre-700 dark:text-lumbre-400 hover:underline">
                  Volver a esta semana
                </button>
              )}
            </div>
            <button onClick={() => irASemana(addWeeks(lunes, 1))} aria-label="Semana siguiente"
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* ── Móvil: tira de días y la lista del elegido ─────────────────── */}
          <div className="lg:hidden">
            <div className="grid grid-cols-7 gap-1 mb-3" role="tablist" aria-label="Días de la semana">
              {dias.map((d, i) => {
                const lista = delDia(d);
                const pendientes = lista.filter(o => o.estado === 'pendiente' || o.estado === 'futura').length;
                const elegido = claveDia(d) === claveDia(diaElegido);
                const esHoy = claveDia(d) === claveDia(hoy);
                return (
                  <button key={i} role="tab" aria-selected={elegido} onClick={() => setDiaElegido(d)}
                    aria-label={format(d, "EEEE d", { locale: es })}
                    className={`flex flex-col items-center py-1.5 rounded-xl border transition ${
                      elegido ? 'bg-lumbre-600 border-lumbre-600 text-white'
                        : esHoy ? 'border-lumbre-300 dark:border-lumbre-700 text-gray-900 dark:text-white'
                        : 'border-transparent text-gray-700 dark:text-gray-300'}`}>
                    <span className="text-[11px] font-semibold opacity-80">{INICIALES_DIA[i]}</span>
                    <span className="text-base font-bold tabular-nums">{d.getDate()}</span>
                    <span className={`text-[10px] tabular-nums h-3 ${elegido ? 'text-white/90' : 'text-gray-500'}`}>
                      {pendientes > 0 ? pendientes : ''}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="space-y-2">
              {delDia(diaElegido).length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">Nada para este día. 🌿</p>
              ) : delDia(diaElegido).map(o => tarjeta(o))}
            </div>
          </div>

          {/* ── iPad y ordenador: los siete días ───────────────────────────── */}
          <div className="hidden lg:grid grid-cols-7 gap-2">
            {dias.map((d, i) => {
              const esHoy = claveDia(d) === claveDia(hoy);
              return (
                <section key={i} className={`min-w-0 rounded-xl p-2 ${
                  esHoy ? 'bg-lumbre-50 dark:bg-lumbre-900/20 ring-1 ring-lumbre-200 dark:ring-lumbre-800' : 'bg-gray-50 dark:bg-gray-800/50'}`}>
                  <h3 className="flex items-baseline justify-between px-1 mb-2">
                    <span className={`text-xs font-bold uppercase tracking-wide ${esHoy ? 'text-lumbre-700 dark:text-lumbre-400' : 'text-gray-500'}`}>
                      {format(d, 'EEE', { locale: es })}
                    </span>
                    <span className="text-sm font-bold tabular-nums text-gray-900 dark:text-white">{d.getDate()}</span>
                  </h3>
                  <div className="space-y-1.5">
                    {delDia(d).map(o => tarjeta(o, { compacta: true }))}
                  </div>
                  <button onClick={() => setNueva({ fecha: d })} aria-label={`Nueva tarea el ${format(d, 'EEEE d', { locale: es })}`}
                    className="w-full mt-1.5 py-1 rounded-lg text-gray-400 hover:text-lumbre-600 hover:bg-white dark:hover:bg-gray-800 transition flex justify-center">
                    <Plus className="w-4 h-4" />
                  </button>
                </section>
              );
            })}
          </div>

          {/* ── Reparto de cada casa compartida (de esta semana) ───────────── */}
          {esEstaSemana && compartidasConTareas.length > 0 && (
            <div className="mt-6 grid gap-3 md:grid-cols-2 items-start">
              {compartidasConTareas.flatMap(c => [
                <TarjetaEstrellas key={`e:${c.id}`} casa={c} tareas={tareas} hechas={hechas}
                  conNombre={compartidasConTareas.length > 1} />,
                <TarjetaReparto key={c.id} casa={c} tareas={tareas} hechas={hechas} conPlus={casaConPlus(c.id)}
                  conNombre={compartidasConTareas.length > 1 || casas.length > 2} />,
              ])}
            </div>
          )}

          <div className="mt-6 text-center">
            <button onClick={() => setPlantilla(true)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-lumbre-700 dark:text-lumbre-400 hover:underline">
              <Sparkles className="w-4 h-4" /> Añadir tareas habituales
            </button>
          </div>
        </>
      )}

      {nueva && casaParaNueva && (
        <ModalTarea casas={casas} casaId={casaParaNueva} fecha={nueva.fecha} onClose={() => setNueva(null)} />
      )}
      {editando && (
        <ModalTarea tarea={editando.tarea} casas={casas} casaId={editando.tarea.casaId} onClose={() => setEditando(null)} />
      )}
      {abierta && (
        <ModalAccionesTarea o={abierta} casa={casaPorId.get(abierta.tarea.casaId)}
          onEditar={() => { setEditando(abierta); setAbierta(null); }} onClose={() => setAbierta(null)} />
      )}
      {plantilla && casaParaNueva && (
        <ModalPlantillaTareas casas={casas} casaId={casaParaNueva} onClose={() => setPlantilla(false)} />
      )}
      {avisos && <ModalAvisosTareas onClose={() => setAvisos(false)} />}
      {calendario && <ModalCalendarioTareas onClose={() => setCalendario(false)} />}
      {borrarVarias && <ModalBorrarTareas casas={casasVistas} onClose={() => setBorrarVarias(false)} />}
    </div>
  );
};
